import { useState } from 'react';
import { Settings, Share2, FileText, Gift } from 'lucide-react';

interface FloatingSideMenuProps {
  onControlsClick: () => void;
  onShareClick: () => void;
  onNotesClick: () => void;
  onGiveawayClick: () => void;
  hasActiveGiveaway?: boolean;
}

const containerStyle: React.CSSProperties = {
  position: 'absolute',
  right: 14,
  top: 'calc(64px + env(safe-area-inset-top, 0px))',
  zIndex: 10,
  display: 'flex',
  flexDirection: 'column',
  gap: 18,
  alignItems: 'center',
  pointerEvents: 'none',
};

const buttonStyle: React.CSSProperties = {
  pointerEvents: 'auto',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 4,
  background: 'transparent',
  border: 'none',
  color: '#fff',
  fontSize: 11,
  fontWeight: 600,
  textShadow: '0 1px 1px rgba(0,0,0,0.25)',
  filter: 'drop-shadow(0 1px 1px rgba(0,0,0,0.2))',
  padding: '4px 2px',
  cursor: 'pointer',
  transition: 'transform 0.1s ease',
};

const giveawayDotStyle: React.CSSProperties = {
  position: 'absolute',
  top: -2,
  right: -2,
  width: 10,
  height: 10,
  borderRadius: '50%',
  background: '#22C55E',
  border: '1.5px solid #1c1c1e',
};

interface SideButtonProps {
  icon: React.ReactNode;
  label: string;
  onClick: () => void;
  badge?: React.ReactNode;
}

function SideButton({ icon, label, onClick, badge }: SideButtonProps) {
  const [hover, setHover] = useState(false);
  const [active, setActive] = useState(false);

  const scale = active ? 'scale(0.95)' : hover ? 'scale(1.06)' : 'scale(1)';

  return (
    <button
      style={{ ...buttonStyle, transform: scale }}
      onClick={onClick}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => { setHover(false); setActive(false); }}
      onMouseDown={() => setActive(true)}
      onMouseUp={() => setActive(false)}
    >
      <div style={{ position: 'relative', display: 'flex' }}>
        {icon}
        {badge}
      </div>
      <span>{label}</span>
    </button>
  );
}

export function FloatingSideMenu({
  onControlsClick,
  onShareClick,
  onNotesClick,
  onGiveawayClick,
  hasActiveGiveaway,
}: FloatingSideMenuProps) {
  return (
    <div style={containerStyle}>
      <SideButton
        icon={<Settings size={22} strokeWidth={1.8} color="#fff" />}
        label="Controls"
        onClick={onControlsClick}
      />
      <SideButton
        icon={<Share2 size={22} strokeWidth={1.8} color="#fff" />}
        label="Share"
        onClick={onShareClick}
      />
      <SideButton
        icon={<FileText size={22} strokeWidth={1.8} color="#fff" />}
        label="Notes"
        onClick={onNotesClick}
      />
      <SideButton
        icon={<Gift size={22} strokeWidth={1.8} color="#fff" />}
        label="Gift"
        onClick={onGiveawayClick}
        badge={hasActiveGiveaway ? <div style={giveawayDotStyle} /> : undefined}
      />
    </div>
  );
}
